import { nextTick, onBeforeUnmount, ref, toValue, watch } from "vue";

function readWaveColors(overrides = {}) {
  if (typeof document === "undefined") {
    return {
      waveColor: overrides.waveColor || "rgba(15, 23, 42, 0.14)",
      progressColor: overrides.progressColor || "#2563eb",
      cursorColor: overrides.cursorColor || overrides.progressColor || "#2563eb",
    };
  }
  const isDark = document.documentElement.classList.contains("dark");
  const root = document.documentElement;
  const progressColor =
    overrides.progressColor ||
    getComputedStyle(root).getPropertyValue("--color-primary-500").trim() ||
    getComputedStyle(root).getPropertyValue("--color-accent-600").trim() ||
    "#2563eb";
  const waveColor =
    overrides.waveColor ||
    (isDark ? "rgba(255,255,255,0.22)" : "rgba(15, 23, 42, 0.14)");
  const cursorColor = overrides.cursorColor || progressColor;
  return { waveColor, progressColor, cursorColor };
}

function resolveHeightOption(options) {
  return toValue(options.height) ?? 20;
}

/**
 * WaveSurfer.js player for compact history / creation audio bars.
 * @param {import('vue').MaybeRefOrGetter<string>} srcSource
 * @param {import('vue').MaybeRefOrGetter<HTMLElement|null>} containerSource
 * @param {{
 *   height?: import('vue').MaybeRefOrGetter<number>,
 *   interact?: import('vue').MaybeRefOrGetter<boolean>,
 *   waveColor?: import('vue').MaybeRefOrGetter<string>,
 *   progressColor?: import('vue').MaybeRefOrGetter<string>,
 *   cursorColor?: import('vue').MaybeRefOrGetter<string>,
 *   cursorWidth?: import('vue').MaybeRefOrGetter<number>,
 *   barWidth?: import('vue').MaybeRefOrGetter<number>,
 *   barGap?: import('vue').MaybeRefOrGetter<number>,
 *   barRadius?: import('vue').MaybeRefOrGetter<number>,
 *   bars?: import('vue').MaybeRefOrGetter<boolean>,
 *   renderFunction?: (peaks: Array<Float32Array | number[]>, ctx: CanvasRenderingContext2D) => void,
 * }} [options]
 */
export function useHistoryAudioWaveform(srcSource, containerSource, options = {}) {
  const loading = ref(false);
  const playing = ref(false);
  const currentTime = ref(0);
  const duration = ref(0);
  const waveformReady = ref(false);

  let wavesurfer = null;
  let objectUrl = "";
  let initGen = 0;
  let resizeObserver = null;

  function revokeObjectUrl() {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = "";
    }
  }

  function disconnectResizeObserver() {
    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }
  }

  function destroyWaveform() {
    disconnectResizeObserver();
    if (wavesurfer) {
      wavesurfer.destroy();
      wavesurfer = null;
    }
    revokeObjectUrl();
    playing.value = false;
    currentTime.value = 0;
    duration.value = 0;
    waveformReady.value = false;
  }

  async function loadWaveformSource(ws, src, gen) {
    try {
      await ws.load(src);
      return;
    } catch {
      /* direct URL failed — retry via blob (CORS / decode) */
    }

    const res = await fetch(src, { mode: "cors" });
    if (!res.ok) throw new Error("fetch failed");
    const blob = await res.blob();
    if (gen !== initGen) throw new Error("stale");
    revokeObjectUrl();
    objectUrl = URL.createObjectURL(blob);
    await ws.load(objectUrl);
  }

  async function initWaveform() {
    const src = String(toValue(srcSource) || "").trim();
    await nextTick();
    let container = toValue(containerSource);

    if (!src || !container || typeof window === "undefined") {
      destroyWaveform();
      loading.value = false;
      return;
    }

    // WaveSurfer paints at 0 width if the flex/grid parent has not laid out yet.
    if (container.clientWidth <= 0) {
      await new Promise((resolve) => {
        const ro = new ResizeObserver(() => {
          if (container.clientWidth > 0) {
            ro.disconnect();
            resolve();
          }
        });
        ro.observe(container);
        requestAnimationFrame(() => {
          if (container.clientWidth > 0) {
            ro.disconnect();
            resolve();
          }
        });
        setTimeout(() => {
          ro.disconnect();
          resolve();
        }, 800);
      });
      container = toValue(containerSource);
      if (!container || container.clientWidth <= 0) {
        loading.value = false;
        return;
      }
    }

    const gen = ++initGen;
    loading.value = true;
    waveformReady.value = false;
    destroyWaveform();
    initGen = gen;

    try {
      const WaveSurfer = (await import("wavesurfer.js")).default;
      await nextTick();
      const mountEl = toValue(containerSource);
      if (gen !== initGen || !mountEl) {
        if (gen === initGen) loading.value = false;
        return;
      }

      mountEl.innerHTML = "";
      // Prefer parent wrap width — mount node can report 0 before WaveSurfer injects children.
      const widthSource =
        mountEl.parentElement?.clientWidth > 0
          ? mountEl.parentElement
          : mountEl;
      const initialWidth = Math.max(1, widthSource.clientWidth || 0);
      const { waveColor, progressColor, cursorColor } = readWaveColors({
        waveColor: toValue(options.waveColor),
        progressColor: toValue(options.progressColor),
        cursorColor: toValue(options.cursorColor),
      });
      const cursorWidth = Number(toValue(options.cursorWidth));
      const useBars = toValue(options.bars) !== false;
      const barWidth = Number(toValue(options.barWidth));
      const barGap = Number(toValue(options.barGap));
      const barRadius = Number(toValue(options.barRadius));
      const resolvedBarWidth =
        Number.isFinite(barWidth) && barWidth > 0 ? barWidth : 2;
      const resolvedBarGap =
        Number.isFinite(barGap) && barGap >= 0 ? barGap : 2;
      const resolvedBarRadius =
        Number.isFinite(barRadius) && barRadius >= 0 ? barRadius : 2;
      const barOptions = useBars
        ? {
            barWidth: resolvedBarWidth,
            barGap: resolvedBarGap,
            barRadius: resolvedBarRadius,
            barMinHeight: 1,
          }
        : {};
      wavesurfer = WaveSurfer.create({
        container: mountEl,
        waveColor,
        progressColor,
        cursorColor,
        cursorWidth: Number.isFinite(cursorWidth) ? cursorWidth : 0,
        ...barOptions,
        height: resolveHeightOption(options),
        width: initialWidth > 1 ? initialWidth : "100%",
        fillParent: true,
        minPxPerSec: 1,
        normalize: true,
        interact: Boolean(toValue(options.interact)),
        ...(typeof options.renderFunction === "function"
          ? { renderFunction: options.renderFunction }
          : {}),
      });

      wavesurfer.on("timeupdate", (time) => {
        if (gen !== initGen) return;
        currentTime.value = time;
      });

      wavesurfer.on("play", () => {
        if (gen !== initGen) return;
        playing.value = true;
      });

      wavesurfer.on("pause", () => {
        if (gen !== initGen) return;
        playing.value = false;
      });

      wavesurfer.on("finish", () => {
        if (gen !== initGen) return;
        playing.value = false;
        currentTime.value = 0;
        wavesurfer.seekTo(0);
      });

      await loadWaveformSource(wavesurfer, src, gen);
      if (gen !== initGen || !wavesurfer) {
        if (gen === initGen) loading.value = false;
        return;
      }

      duration.value = wavesurfer.getDuration() || 0;
      let lastWidth = 0;
      const syncWidth = () => {
        const el = toValue(containerSource);
        if (!el || !wavesurfer || gen !== initGen) return;
        const measureEl =
          el.parentElement?.clientWidth > 0 ? el.parentElement : el;
        const w = Math.round(measureEl.clientWidth);
        if (w <= 0 || Math.abs(w - lastWidth) < 1) return;
        lastWidth = w;
        try {
          wavesurfer.setOptions({
            width: w,
            fillParent: true,
            minPxPerSec: 1,
            ...(useBars
              ? {
                  barWidth: resolvedBarWidth,
                  barGap: resolvedBarGap,
                  barRadius: resolvedBarRadius,
                }
              : {}),
          });
        } catch {
          /* ignore */
        }
      };
      // Layout may settle after paint — sync twice so density matches real width.
      syncWidth();
      requestAnimationFrame(() => {
        syncWidth();
        requestAnimationFrame(syncWidth);
      });
      loading.value = false;
      waveformReady.value = true;

      disconnectResizeObserver();
      if (typeof ResizeObserver !== "undefined") {
        const el = toValue(containerSource);
        const observeTarget = el?.parentElement || el;
        if (observeTarget) {
          resizeObserver = new ResizeObserver(() => {
            if (gen !== initGen || !wavesurfer) {
              disconnectResizeObserver();
              return;
            }
            syncWidth();
          });
          resizeObserver.observe(observeTarget);
        }
      }
    } catch {
      if (gen === initGen) {
        loading.value = false;
        waveformReady.value = false;
      }
    }
  }

  function togglePlayback() {
    if (wavesurfer && waveformReady.value) {
      wavesurfer.playPause();
      return false;
    }
    return true;
  }

  function pausePlayback() {
    if (wavesurfer && waveformReady.value && playing.value) {
      wavesurfer.pause();
      return;
    }
    playing.value = false;
  }

  watch(
    () => [toValue(srcSource), toValue(containerSource), resolveHeightOption(options)],
    () => {
      initWaveform();
    },
    { flush: "post" },
  );

  onBeforeUnmount(() => {
    initGen += 1;
    destroyWaveform();
  });

  return {
    loading,
    playing,
    currentTime,
    duration,
    waveformReady,
    togglePlayback,
    pausePlayback,
    initWaveform,
    destroyWaveform,
  };
}
